/**
 * PayoutProfile.jsx — Bidder sets up bank account / UPI for receiving payouts.
 * Shows verification status prominently so bidder knows if they're ready to receive funds.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress,
  Divider, Paper, TextField, Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VerifiedIcon from '@mui/icons-material/Verified';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { apiClient } from '../../utils/auth';

const STATUS_CONFIG = {
  VERIFIED: {
    severity: 'success',
    icon: <VerifiedIcon fontSize="small" />,
    label: 'Account Verified',
    description: 'Your payout profile has been verified. You are ready to receive funds.',
    chipColor: 'success',
  },
  PENDING: {
    severity: 'warning',
    icon: <HourglassEmptyIcon fontSize="small" />,
    label: 'Verification Pending',
    description: 'Your payout profile is under review. Payouts will be enabled once verified.',
    chipColor: 'warning',
  },
  REJECTED: {
    severity: 'error',
    icon: <ErrorOutlineIcon fontSize="small" />,
    label: 'Verification Failed',
    description: 'Your payout profile could not be verified. Please update your details.',
    chipColor: 'error',
  },
};

export default function PayoutProfile() {
  const [form, setForm] = useState({ account_number: '', ifsc_code: '', upi_id: '', dummy_account_number: '', dummy_ifsc_code: '' });
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadProfile = () => {
    setLoading(true);
    apiClient.get('/api/v1/payment/payout-profile/')
      .then(({ data }) => {
        if (data.ifsc_code) {
          setExisting(data);
          if (data.has_dummy_account) {
            setForm(f => ({ ...f, dummy_ifsc_code: data.dummy_ifsc_code }));
          }
        }
        else setExisting(null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProfile(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const { data } = await apiClient.post('/api/v1/payment/payout-profile/', form);
      setSuccess(data.message || 'Payout profile saved successfully.');
      setExisting({
        ifsc_code: form.ifsc_code,
        upi_id: form.upi_id,
        has_account_number: true,
        verification_status: data.verification_status,
        verification_remarks: data.verification_remarks,
        has_dummy_account: !!form.dummy_account_number,
        dummy_ifsc_code: form.dummy_ifsc_code,
      });
      setForm(f => ({ ...f, account_number: '', ifsc_code: '', upi_id: '' })); // keep dummy fields for next edit if needed
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const statusInfo = existing ? STATUS_CONFIG[existing.verification_status] || STATUS_CONFIG.PENDING : null;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AccountBalanceIcon color="primary" />
        <Typography variant="h5">Payout Profile</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Add your bank account details to receive payments. Your account number is stored encrypted.
      </Typography>

      {/* Verification Status Banner */}
      {existing && statusInfo && (
        <Paper
          variant="outlined"
          sx={{
            p: 2, mb: 3,
            borderColor: existing.verification_status === 'VERIFIED' ? 'success.main'
              : existing.verification_status === 'REJECTED' ? 'error.main' : 'warning.main',
            bgcolor: existing.verification_status === 'VERIFIED' ? 'rgba(16, 185, 129, 0.08)'
              : existing.verification_status === 'REJECTED' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.label}
              color={statusInfo.chipColor}
              size="small"
              variant="filled"
            />
            <Typography variant="caption" color="text.secondary">
              IFSC: <strong>{existing.ifsc_code}</strong>
              {existing.upi_id ? ` · UPI: ${existing.upi_id}` : ''}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {statusInfo.description}
          </Typography>
          {existing.verification_remarks && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
              Remarks: {existing.verification_remarks}
            </Typography>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {existing ? 'Update Payout Details' : 'Add Payout Details'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Bank Account Number *"
            value={form.account_number}
            onChange={(e) => setForm(f => ({ ...f, account_number: e.target.value }))}
            fullWidth margin="normal" required
            inputProps={{ maxLength: 18, 'aria-label': 'Bank account number' }}
            helperText="9–18 digit account number"
          />
          <TextField
            label="IFSC Code *"
            value={form.ifsc_code}
            onChange={(e) => setForm(f => ({ ...f, ifsc_code: e.target.value.toUpperCase() }))}
            fullWidth margin="normal" required
            inputProps={{ maxLength: 11, 'aria-label': 'IFSC code' }}
            helperText="Format: ABCD0123456"
          />
          <Divider sx={{ my: 2 }} />
          <TextField
            label="UPI ID (optional)"
            value={form.upi_id}
            onChange={(e) => setForm(f => ({ ...f, upi_id: e.target.value }))}
            fullWidth margin="normal"
            inputProps={{ maxLength: 100, 'aria-label': 'UPI ID' }}
            helperText="Format: yourname@upi"
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 1 }}>{success}</Alert>}
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : existing ? 'Update Payout Profile' : 'Save Payout Profile'}
          </Button>

          {/* Test Environment Settings */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 2 }}>
            Developer Settings (Razorpay Simulation)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Add a dummy account to simulate Razorpay payouts. This is temporary and kept separate from your real bank details.
          </Typography>
          <TextField
            label="Dummy Account Number"
            value={form.dummy_account_number}
            onChange={(e) => setForm(f => ({ ...f, dummy_account_number: e.target.value }))}
            fullWidth margin="normal"
            inputProps={{ maxLength: 18, 'aria-label': 'Dummy Bank account number' }}
            helperText="Account number for Razorpay payout tests"
          />
          <TextField
            label="Dummy IFSC Code"
            value={form.dummy_ifsc_code}
            onChange={(e) => setForm(f => ({ ...f, dummy_ifsc_code: e.target.value.toUpperCase() }))}
            fullWidth margin="normal"
            inputProps={{ maxLength: 11, 'aria-label': 'Dummy IFSC code' }}
            helperText="e.g. RATN0VAAPIS for Razorpay API testing"
          />
        </Box>
      </Paper>
    </Box>
  );
}
